import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';

import { PARTNER_ENTRANCE } from '../../../../util/partnerEntrance';

import { NamedLink, IconArrowHead } from '../../../../components';

import {
  visibleCategories,
  categorySearchString,
  hasChildren,
} from '../../../../components/CategoryMenu/helpers';

import css from './MobileCategoryTree.module.css';

/**
 * Mobile category tree that drills one level at a time.
 *
 * State is the current `path` (array of ancestor category ids). At the
 * root, `path` is empty and we render the Level 1 categories. Tapping a
 * branch row appends its id to the path; tapping a leaf row navigates
 * straight to SearchPage with the matching `pub_categoryLevel*` params
 * and dismisses the menu via `onLinkClick`.
 *
 * Every non-root level renders:
 *   - A "← {parent name}" back row that pops one segment off the path
 *   - A "See all {current category}" link that goes to SearchPage with
 *     the current path's params
 *   - The visible subcategories under the current node
 *
 * @param {Object} props
 * @param {Array} props.categories merged category tree (already
 *                                 sourced via useConfiguration)
 * @param {Function} props.onLinkClick called after a leaf link is
 *                                     tapped so the parent menu can
 *                                     close itself
 */
const MobileCategoryTree = props => {
  const { categories, onLinkClick } = props;
  const intl = useIntl();
  const [path, setPath] = useState([]);

  const visibleRoots = visibleCategories(categories);

  // Walk the visible tree following the current `path`. `currentNodes`
  // is the list rendered at this level; `currentParent` is the node
  // representing the current location (or null at root).
  let currentNodes = visibleRoots;
  let currentParent = null;
  let parentChain = [];
  for (const id of path) {
    const match = currentNodes.find(c => c.id === id);
    if (!match) {
      break;
    }
    parentChain.push(match);
    currentParent = match;
    currentNodes = match.subcategories || [];
  }

  const handleBack = () => {
    setPath(prev => prev.slice(0, prev.length - 1));
  };

  const handleDrill = id => {
    setPath(prev => [...prev, id]);
  };

  return (
    <div className={css.root}>
      {currentParent ? (
        <button type="button" className={css.backRow} onClick={handleBack}>
          <IconArrowHead direction="left" size="small" className={css.backIcon} />
          <span className={css.backLabel}>{currentParent.name}</span>
        </button>
      ) : null}

      {currentParent ? (
        // NamedLink doesn't forward onClick; wrap with a div so the
        // click bubbles up and dismisses the mobile menu.
        <div onClick={onLinkClick}>
          <NamedLink
            className={css.allLink}
            name="SearchPage"
            to={{ search: categorySearchString(path) }}
          >
            <FormattedMessage
              id="CategoryMenu.viewAllIn"
              values={{
                category: currentParent.name,
                quantifier: currentParent.viewAllQuantifier,
              }}
            />
          </NamedLink>
        </div>
      ) : null}

      <div className={classNames(css.list, { [css.listNested]: !!currentParent })}>
        {currentNodes.map(item => {
          const itemHasChildren = hasChildren(item);
          const isRootLevel = path.length === 0;
          if (itemHasChildren) {
            return (
              <button
                key={item.id}
                type="button"
                className={isRootLevel ? css.branchRowRoot : css.branchRow}
                onClick={() => handleDrill(item.id)}
                aria-label={intl.formatMessage(
                  { id: 'CategoryMenu.openSubcategoryAria' },
                  { category: item.name }
                )}
              >
                <span className={css.itemLabel}>{item.name}</span>
                <IconArrowHead direction="right" size="small" className={css.branchIcon} />
              </button>
            );
          }
          return (
            // NamedLink doesn't forward onClick; wrap so the click
            // bubbles up to dismiss the mobile menu.
            <div key={item.id} onClick={onLinkClick}>
              <NamedLink
                className={css.leafRow}
                name="SearchPage"
                to={{ search: categorySearchString([...path, item.id]) }}
              >
                <span className={css.itemLabel}>{item.name}</span>
              </NamedLink>
            </div>
          );
        })}
        {/* After the categories, at the top level: the way into Zuprize
            Partner, as on the homepage and in the desktop menu. */}
        {currentParent ? null : (
          <div onClick={onLinkClick}>
            <NamedLink className={css.leafRowRoot} name={PARTNER_ENTRANCE.routeName}>
              <span className={css.itemLabel}>
                <FormattedMessage id={PARTNER_ENTRANCE.labelId} />
              </span>
            </NamedLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileCategoryTree;
